import { useState, useEffect } from "react";
import { api, formatCurrency, formatPercent, monthNames } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { format } from "date-fns";
import { fr } from "date-fns/locale";
import { Loader2, Download, DollarSign, CalendarIcon, Percent, TrendingUp, FileDown, CheckCircle2, AlertTriangle, Archive } from "lucide-react";
import { exportToPDF } from "@/lib/pdf";
import { CommissionValidationDialog } from "@/components/CommissionValidationDialog";
import { CommissionsChart } from "@/components/CommissionsChart";
import { UpcomingCommissionsCard } from "@/components/UpcomingCommissionsCard";

export const CommissionsPage = () => {
  const { isAdmin } = useAuth();
  const [sellerCommissions, setSellerCommissions] = useState(null);
  const [managerCommission, setManagerCommission] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth() + 1);
  const [rateDialogOpen, setRateDialogOpen] = useState(false);
  const [rateData, setRateData] = useState({ rate: 1, effective_date: new Date() });
  const [validationOrderId, setValidationOrderId] = useState(null);
  const [sellersList, setSellersList] = useState([]);
  const [archivedSellers, setArchivedSellers] = useState([]);
  const [archivedLoading, setArchivedLoading] = useState(false);

  useEffect(() => {
    api.getSellers().then((r) => setSellersList(r.data)).catch(() => {});
  }, []);

  const fetchArchivedSellers = async () => {
    try {
      setArchivedLoading(true);
      const res = await api.getArchivedSellersCommissions();
      setArchivedSellers(res.data?.sellers || []);
    } catch {
      setArchivedSellers([]);
    } finally {
      setArchivedLoading(false);
    }
  };

  useEffect(() => {
    fetchArchivedSellers();
  }, []);

  const currentYear = new Date().getFullYear();
  const years = Array.from({ length: currentYear - 2015 + 1 }, (_, i) => currentYear - i);

  const fetchCommissions = async () => {
    try {
      setLoading(true);
      const [sellersRes, managerRes] = await Promise.all([
        api.getSellerCommissions({ year: selectedYear, month: selectedMonth }),
        api.getManagerCommission({ year: selectedYear, month: selectedMonth })
      ]);
      setSellerCommissions(sellersRes.data);
      setManagerCommission(managerRes.data);
    } catch (error) {
      toast.error("Erreur lors du chargement des commissions");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCommissions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedYear, selectedMonth]);

  const handleManagerRateSubmit = async (e) => {
    e.preventDefault();
    try {
      await api.createCommissionRate({
        user_id: null,
        rate: parseFloat(rateData.rate) / 100,
        effective_date: rateData.effective_date.toISOString().split("T")[0],
        commission_type: "manager"
      });
      toast.success("Taux de commission gérant mis à jour");
      setRateDialogOpen(false);
      fetchCommissions();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erreur");
    }
  };

  const handleExport = async () => {
    try {
      const response = await api.exportCommissions({ year: selectedYear, month: selectedMonth });
      const data = response.data.data;
      
      const headers = Object.keys(data[0] || {}).join(";");
      const rows = data.map(row => Object.values(row).join(";")).join("\n");
      const csv = `${headers}\n${rows}`;
      
      const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `commissions_${selectedYear}_${selectedMonth}.csv`;
      link.click();
      
      toast.success("Export réussi");
    } catch (error) {
      toast.error("Erreur lors de l'export");
    }
  };

  const totalSellerCommissions = sellerCommissions?.commissions?.reduce(
    (sum, c) => sum + c.payable_commission, 0
  ) || 0;

  return (
    <div className="space-y-6 animate-in" data-testid="commissions-page" id="commissions-content">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="font-heading text-2xl sm:text-3xl font-bold">Commissions</h1>
          <p className="text-muted-foreground mt-1">
            Suivi des commissions vendeurs et gérant
          </p>
        </div>
        <div className="flex items-center gap-3">
          <Select value={String(selectedMonth)} onValueChange={(v) => setSelectedMonth(Number(v))}>
            <SelectTrigger className="w-40" data-testid="commission-month-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {monthNames.map((name, idx) => (
                <SelectItem key={idx} value={String(idx + 1)}>{name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={String(selectedYear)} onValueChange={(v) => setSelectedYear(Number(v))}>
            <SelectTrigger className="w-28" data-testid="commission-year-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {years.map((year) => (
                <SelectItem key={year} value={String(year)}>{year}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Button onClick={handleExport} variant="outline" data-testid="export-commissions-btn">
            <Download className="h-4 w-4 mr-2" />
            CSV
          </Button>
          <Button
            onClick={() => exportToPDF("commissions-content", "commissions-chantierflow", `Commissions ${monthNames[selectedMonth - 1]} ${selectedYear}`).catch(() => toast.error("Erreur d'export PDF"))}
            variant="outline"
            data-testid="export-commissions-pdf"
          >
            <FileDown className="h-4 w-4 mr-2" />
            PDF
          </Button>
        </div>
      </div>

      {loading ? (
        <div className="flex items-center justify-center h-64">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
        </div>
      ) : (
        <Tabs defaultValue="sellers" className="space-y-6" onValueChange={(v) => { if (v === "archived") fetchArchivedSellers(); }}>
          <TabsList>
            <TabsTrigger value="sellers" data-testid="tab-sellers">Vendeurs</TabsTrigger>
            <TabsTrigger value="manager" data-testid="tab-manager">Gérant</TabsTrigger>
            <TabsTrigger value="archived" data-testid="tab-archived">
              <Archive className="h-4 w-4 mr-1" /> Archivés
              {archivedSellers.length > 0 && (
                <Badge variant="outline" className="ml-2 h-5 min-w-5 text-xs px-1 border-slate-400 text-slate-600">
                  {archivedSellers.length}
                </Badge>
              )}
            </TabsTrigger>
          </TabsList>

          {/* Sellers Commissions */}
          <TabsContent value="sellers" className="space-y-6">
            {/* Summary */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <Card className="kpi-card">
                <CardContent className="pt-6">
                  <div className="flex items-center gap-4">
                    <div className="h-12 w-12 bg-primary/10 rounded-sm flex items-center justify-center">
                      <DollarSign className="h-6 w-6 text-primary" />
                    </div>
                    <div>
                      <p className="text-sm text-muted-foreground">Commissions payables</p>
                      <p className="text-2xl font-heading font-bold currency">
                        {formatCurrency(totalSellerCommissions)}
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card className="kpi-card">
                <CardContent className="pt-6">
                  <div className="flex items-center gap-4">
                    <div className="h-12 w-12 bg-secondary/10 rounded-sm flex items-center justify-center">
                      <TrendingUp className="h-6 w-6 text-secondary" />
                    </div>
                    <div>
                      <p className="text-sm text-muted-foreground">Total CA vendeurs</p>
                      <p className="text-2xl font-heading font-bold currency">
                        {formatCurrency(
                          sellerCommissions?.commissions?.reduce((s, c) => s + c.total_amount, 0) || 0
                        )}
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <UpcomingCommissionsCard />
            </div>

            {/* Chart */}
            <CommissionsChart sellers={sellersList} />

            {/* Sellers Table */}
            <Card>
              <CardHeader>
                <CardTitle className="font-heading">
                  Commissions vendeurs - {monthNames[selectedMonth - 1]} {selectedYear}
                </CardTitle>
              </CardHeader>
              <CardContent className="p-0">
                {!sellerCommissions?.commissions?.length ? (
                  <div className="text-center py-12">
                    <DollarSign className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
                    <p className="text-muted-foreground">Aucune commission pour cette période</p>
                  </div>
                ) : (
                  <div className="p-6 space-y-6">
                    {sellerCommissions.commissions.map((comm) => (
                      <div key={comm.seller_id} className="border rounded-sm" data-testid={`commission-row-${comm.seller_id}`}>
                        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 p-4 bg-muted/30 border-b">
                          <div>
                            <p className="font-heading font-bold">{comm.seller_name}</p>
                            <p className="text-xs text-muted-foreground">
                              Taux {formatPercent(comm.rate)} • {comm.total_orders} commande(s) terminée(s)
                              {comm.pending_validation > 0 && (
                                <Badge variant="outline" className="ml-2 border-amber-400 text-amber-700">
                                  {comm.pending_validation} à valider
                                </Badge>
                              )}
                            </p>
                          </div>
                          <div className="flex gap-6 text-sm">
                            <div>
                              <p className="text-xs text-muted-foreground">Payé HT (base commission)</p>
                              <p className="currency font-medium">{formatCurrency(comm.total_paid_ht ?? comm.total_paid)}</p>
                            </div>
                            <div>
                              <p className="text-xs text-muted-foreground">Commission payable</p>
                              <p className="currency font-bold text-green-600">{formatCurrency(comm.payable_commission)}</p>
                            </div>
                          </div>
                        </div>

                        {comm.orders.length === 0 ? (
                          <p className="p-4 text-sm text-muted-foreground">Aucune commande terminée sur cette période.</p>
                        ) : (
                          <div className="overflow-x-auto">
                            <table className="data-table">
                              <thead>
                                <tr>
                                  <th>Référence</th>
                                  <th>Client</th>
                                  <th>Encaissée le</th>
                                  <th>Montant HT</th>
                                  <th>Payé TTC</th>
                                  <th>Payé HT</th>
                                  <th>Commission brute</th>
                                  <th>Ratio critères</th>
                                  <th>Commission finale</th>
                                  <th>Validation</th>
                                  <th></th>
                                </tr>
                              </thead>
                              <tbody>
                                {comm.orders.map((o) => (
                                  <tr key={o.order_id} data-testid={`order-commission-${o.order_id}`}>
                                    <td className="font-medium">{o.reference}</td>
                                    <td>{o.client_name}</td>
                                    <td className="text-xs text-muted-foreground whitespace-nowrap" data-testid={`order-paid-date-${o.order_id}`}>
                                      {(o.paid_date || o.completed_at)
                                        ? new Date(o.paid_date || o.completed_at).toLocaleDateString("fr-FR")
                                        : "—"}
                                    </td>
                                    <td className="currency">{formatCurrency(o.amount_ht)}</td>
                                    <td className="currency">{formatCurrency(o.paid_amount)}</td>
                                    <td className="currency">{formatCurrency(o.paid_amount_ht ?? o.paid_amount)}</td>
                                    <td className="currency">{formatCurrency(o.gross_commission)}</td>
                                    <td>{formatPercent(o.ratio)}</td>
                                    <td className="currency font-medium text-green-600">
                                      {formatCurrency(o.final_commission)}
                                    </td>
                                    <td>
                                      {o.validated ? (
                                        <Badge variant="outline" className="border-green-400 text-green-700">
                                          <CheckCircle2 className="h-3 w-3 mr-1" /> Validé
                                        </Badge>
                                      ) : (
                                        <Badge variant="outline" className="border-amber-400 text-amber-700">
                                          <AlertTriangle className="h-3 w-3 mr-1" /> À valider
                                        </Badge>
                                      )}
                                    </td>
                                    <td>
                                      <Button
                                        variant="outline"
                                        size="sm"
                                        onClick={() => setValidationOrderId(o.order_id)}
                                        data-testid={`validate-commission-${o.order_id}`}
                                      >
                                        {o.validated ? "Modifier" : "Valider"}
                                      </Button>
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>

            <Card className="bg-muted/30">
              <CardContent className="pt-6">
                <p className="text-sm text-muted-foreground">
                  <strong>Note :</strong> La commission n'est due que pour les commandes <strong>terminées</strong>.
                  Elle est calculée sur le <strong>montant effectivement payé</strong> (somme des paiements reçus),
                  multiplié par le taux du vendeur puis par le ratio des critères validés (poids cochés / poids total).
                  Définissez les critères par vendeur depuis la page <strong>Vendeurs</strong>.
                </p>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Manager Commission */}
          <TabsContent value="manager" className="space-y-6">
            <Card className="kpi-card">
              <CardContent className="pt-6">
                <div className="flex items-start justify-between">
                  <div className="flex items-center gap-4">
                    <div className="h-16 w-16 bg-primary/10 rounded-sm flex items-center justify-center">
                      <Percent className="h-8 w-8 text-primary" />
                    </div>
                    <div>
                      <p className="text-sm text-muted-foreground">Ma commission - {monthNames[selectedMonth - 1]} {selectedYear}</p>
                      <p className="text-4xl font-heading font-bold currency mt-1">
                        {formatCurrency(managerCommission?.commission || 0)}
                      </p>
                      <p className="text-sm text-muted-foreground mt-2">
                        {formatPercent(managerCommission?.rate || 0.01)} du CA réalisé ({formatCurrency(managerCommission?.total_ca || 0)})
                      </p>
                    </div>
                  </div>
                  {isAdmin && (
                    <Button
                      variant="outline"
                      onClick={() => {
                        setRateData({
                          rate: (managerCommission?.rate || 0.01) * 100,
                          effective_date: new Date()
                        });
                        setRateDialogOpen(true);
                      }}
                      data-testid="edit-manager-rate-btn"
                    >
                      <Percent className="h-4 w-4 mr-2" />
                      Modifier mon taux
                    </Button>
                  )}
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="font-heading">Détail du calcul</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <div className="flex justify-between items-center py-3 border-b">
                    <span className="text-muted-foreground">CA réalisé du mois (importé + avancement)</span>
                    <span className="font-medium currency">{formatCurrency(managerCommission?.total_ca || 0)}</span>
                  </div>
                  {managerCommission?.total_orders_amount > 0 && (
                    <div className="flex justify-between items-center py-3 border-b text-sm">
                      <span className="text-muted-foreground">Montant des commandes du mois (pour info)</span>
                      <span className="text-muted-foreground currency">{formatCurrency(managerCommission?.total_orders_amount || 0)}</span>
                    </div>
                  )}
                  <div className="flex justify-between items-center py-3 border-b">
                    <span className="text-muted-foreground">Taux de commission</span>
                    <span className="font-medium">{formatPercent(managerCommission?.rate || 0.01)}</span>
                  </div>
                  <div className="flex justify-between items-center py-3 bg-primary/5 px-4 -mx-4 rounded">
                    <span className="font-medium">Commission à percevoir</span>
                    <span className="font-heading text-xl font-bold text-primary currency">
                      {formatCurrency(managerCommission?.commission || 0)}
                    </span>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="bg-muted/30">
              <CardContent className="pt-6">
                <p className="text-sm text-muted-foreground">
                  <strong>Note :</strong> La commission du gérant est calculée sur le <strong>CA réalisé</strong>, 
                  c'est-à-dire le chiffre d'affaires effectivement comptabilisé (imports historiques + avancement des commandes), 
                  et non sur le montant total des nouvelles commandes du mois.
                </p>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Archived Sellers */}
          <TabsContent value="archived" className="space-y-4">
            <Card>
              <CardHeader>
                <CardTitle className="font-heading flex items-center gap-2">
                  <Archive className="h-5 w-5 text-slate-500" />
                  Historique des vendeurs archivés
                </CardTitle>
                <p className="text-sm text-muted-foreground mt-1">
                  Commissions cumulées et nombre de commandes à vie pour chaque ex-vendeur.
                  Utile pour les régularisations de fin d'année et les audits.
                </p>
              </CardHeader>
              <CardContent className="p-0">
                {archivedLoading ? (
                  <div className="py-12 text-center">
                    <Loader2 className="h-8 w-8 animate-spin text-primary mx-auto" />
                  </div>
                ) : archivedSellers.length === 0 ? (
                  <div className="py-12 text-center">
                    <Archive className="h-12 w-12 mx-auto text-muted-foreground mb-3" />
                    <p className="text-sm text-muted-foreground">Aucun vendeur archivé.</p>
                    <p className="text-xs text-muted-foreground mt-1">
                      Archivez un vendeur depuis la page <strong>Vendeurs</strong> pour préserver son
                      historique sans polluer les vues actives.
                    </p>
                  </div>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead className="bg-slate-50 border-b">
                        <tr>
                          <th className="text-left px-4 py-3 font-medium">Vendeur</th>
                          <th className="text-left px-4 py-3 font-medium">Salarié lié</th>
                          <th className="text-left px-4 py-3 font-medium">Archivé le</th>
                          <th className="text-left px-4 py-3 font-medium">Période d'activité</th>
                          <th className="text-right px-4 py-3 font-medium">Commandes</th>
                          <th className="text-right px-4 py-3 font-medium">CA HT payé</th>
                          <th className="text-right px-4 py-3 font-medium">Commissions à vie</th>
                        </tr>
                      </thead>
                      <tbody>
                        {archivedSellers.map((s) => (
                          <tr
                            key={s.seller_id}
                            className="border-b hover:bg-slate-50/50"
                            data-testid={`archived-seller-row-${s.seller_id}`}
                          >
                            <td className="px-4 py-3">
                              <div className="flex items-center gap-2">
                                <div className="h-8 w-8 rounded-full bg-slate-100 flex items-center justify-center text-xs font-bold text-slate-600">
                                  {(s.seller_name || "?").charAt(0).toUpperCase()}
                                </div>
                                <div>
                                  <div className="font-medium">{s.seller_name || "—"}</div>
                                  <Badge variant="outline" className="border-slate-400 text-slate-600 text-[10px] mt-0.5">
                                    Archivé
                                  </Badge>
                                </div>
                              </div>
                            </td>
                            <td className="px-4 py-3 text-muted-foreground">
                              {s.employee_name || "—"}
                            </td>
                            <td className="px-4 py-3 text-xs text-muted-foreground">
                              {s.archived_at
                                ? new Date(s.archived_at).toLocaleDateString("fr-FR")
                                : "—"}
                            </td>
                            <td className="px-4 py-3 text-xs text-muted-foreground">
                              {s.first_order_date && s.last_order_date
                                ? `${new Date(s.first_order_date).toLocaleDateString("fr-FR")} → ${new Date(s.last_order_date).toLocaleDateString("fr-FR")}`
                                : "—"}
                            </td>
                            <td className="px-4 py-3 text-right font-medium">{s.orders_count}</td>
                            <td className="px-4 py-3 text-right text-muted-foreground">
                              {formatCurrency(s.lifetime_paid_ht)}
                            </td>
                            <td className="px-4 py-3 text-right font-bold text-indigo-700">
                              {formatCurrency(s.lifetime_commission)}
                            </td>
                          </tr>
                        ))}
                        {/* Total row */}
                        <tr className="bg-indigo-50/40 font-semibold">
                          <td className="px-4 py-3" colSpan={4}>
                            Total ({archivedSellers.length} vendeur{archivedSellers.length > 1 ? "s" : ""})
                          </td>
                          <td className="px-4 py-3 text-right">
                            {archivedSellers.reduce((s, x) => s + (x.orders_count || 0), 0)}
                          </td>
                          <td className="px-4 py-3 text-right">
                            {formatCurrency(archivedSellers.reduce((s, x) => s + (x.lifetime_paid_ht || 0), 0))}
                          </td>
                          <td className="px-4 py-3 text-right text-indigo-700">
                            {formatCurrency(archivedSellers.reduce((s, x) => s + (x.lifetime_commission || 0), 0))}
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      )}

      {/* Manager Rate Dialog */}
      <Dialog open={rateDialogOpen} onOpenChange={setRateDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="font-heading">Modifier mon taux de commission</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleManagerRateSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="manager_rate">Nouveau taux (%)</Label>
              <Input
                id="manager_rate"
                type="number"
                step="0.1"
                min="0"
                max="100"
                value={rateData.rate}
                onChange={(e) => setRateData({ ...rateData, rate: e.target.value })}
                required
                data-testid="manager-rate-input"
              />
            </div>
            <div className="space-y-2">
              <Label>Date d'effet</Label>
              <Popover>
                <PopoverTrigger asChild>
                  <Button variant="outline" className="w-full justify-start">
                    <CalendarIcon className="mr-2 h-4 w-4" />
                    {format(rateData.effective_date, "PPP", { locale: fr })}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0">
                  <Calendar
                    mode="single"
                    selected={rateData.effective_date}
                    onSelect={(d) => d && setRateData({ ...rateData, effective_date: d })}
                    locale={fr}
                  />
                </PopoverContent>
              </Popover>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setRateDialogOpen(false)}>
                Annuler
              </Button>
              <Button type="submit" data-testid="manager-rate-submit-btn">
                Enregistrer
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <CommissionValidationDialog
        open={!!validationOrderId}
        orderId={validationOrderId}
        onOpenChange={(v) => { if (!v) setValidationOrderId(null); }}
        onSaved={fetchCommissions}
      />
    </div>
  );
};

export default CommissionsPage;
